import React, { useContext } from 'react';
import {
  CirculationTitle,
  SmallCard,
  SmallCardTitle,
} from 'spw-components/project-details-tabs/program-summary/esa-single-page-summary/styles';
import { Grid } from '@mui/material';
import {
  BreakdownColumn,
  BreakdownRow,
  BreakdownTotalRow,
  PercentValue,
} from 'spw-programming-projects/program-summary/components/styles';
import {
  asPercentageRounded,
  numberWithCommas,
  numberWithCommasOrDash,
  textOrDash,
  toFixed,
} from 'spw-shared/utils';
import { ProgramSummaryCardsContext } from 'spw-esa-projects/program-summary/summary-cards';
import { useSelector } from 'react-redux';
import { selectRsfTotal } from 'spw-redux/modules/program-summary/esa/selectors';
import { colors } from 'spw-theme';
import useUnitConversion from 'spw-components/unit-conversion/hooks';
import { MeasurementSystem } from 'spw-components/unit-conversion/types';

export const USFBreakdownCard = ({
  subtotal,
  circulationPercent,
  circulation,
  total,
}: {
  subtotal: number;
  circulationPercent: number;
  circulation: number;
  total: number;
}) => {
  const { getUnit, convertArea, measurementSystem } = useUnitConversion();
  const { showRsf } = useContext(ProgramSummaryCardsContext);
  const { totalRsf, buildingRsfLoadFactor } = useSelector(selectRsfTotal);
  const deltaUsf = total - subtotal;
  const deltaRsf = totalRsf ? totalRsf - total : 0;

  return (
    <SmallCard data-testid="usf_breakdown_card" $tall={showRsf}>
      <SmallCardTitle>
        Square{' '}
        {measurementSystem === MeasurementSystem.imperial
          ? 'Footage'
          : 'Meters'}{' '}
        Breakdown
      </SmallCardTitle>
      <Grid container>
        <Grid item xs={12}>
          <BreakdownRow>
            <BreakdownColumn item xs={8}>
              Subtotal {getUnit('area')}
            </BreakdownColumn>
            <BreakdownColumn
              item
              xs={4}
              className="right"
              data-testid="subtotal-usf"
            >
              {numberWithCommasOrDash(convertArea(subtotal))}
            </BreakdownColumn>
          </BreakdownRow>

          <BreakdownRow className="last">
            <BreakdownColumn item xs={8}>
              <CirculationTitle>
                Circulation @
                <PercentValue data-testid="circulation_percent">
                  {asPercentageRounded(circulationPercent, true)}
                </PercentValue>
              </CirculationTitle>
            </BreakdownColumn>
            <BreakdownColumn
              item
              xs={4}
              className="right"
              data-test="circulation"
            >
              {circulation
                ? numberWithCommas(toFixed(convertArea(circulation)))
                : textOrDash(circulation)}
            </BreakdownColumn>
          </BreakdownRow>

          <BreakdownTotalRow style={showRsf ? undefined : { marginBottom: 0 }}>
            <BreakdownColumn item xs={8}>
              Total {getUnit('area')}
            </BreakdownColumn>
            <BreakdownColumn
              item
              xs={4}
              className={`right ${deltaUsf < 0 ? 'error' : ''}`}
              data-testid="total-usf"
            >
              {numberWithCommasOrDash(convertArea(total))}
            </BreakdownColumn>
          </BreakdownTotalRow>

          {showRsf && (
            <>
              <BreakdownRow>
                <BreakdownColumn item xs={8}>
                  Rentable Load Factor @
                  <PercentValue data-testid="esa_rsf_load_factor_percentage">
                    {asPercentageRounded(
                      buildingRsfLoadFactor > 0 ? buildingRsfLoadFactor : NaN,
                      true
                    )}
                  </PercentValue>
                </BreakdownColumn>
                <BreakdownColumn
                  item
                  xs={4}
                  className="right"
                  data-testid="rsf_load_factor"
                >
                  {numberWithCommasOrDash(
                    deltaRsf > 0 ? convertArea(deltaRsf) : NaN
                  )}
                </BreakdownColumn>
              </BreakdownRow>
              <BreakdownRow>
                <BreakdownColumn item xs={8}>
                  Total {getUnit('rentable-area')}
                </BreakdownColumn>
                <BreakdownColumn
                  item
                  xs={4}
                  className="right"
                  data-testid="total_rsf"
                  style={{
                    color: totalRsf < total ? colors.red : colors.black,
                  }}
                >
                  {numberWithCommasOrDash(convertArea(totalRsf))}
                </BreakdownColumn>
              </BreakdownRow>
            </>
          )}
        </Grid>
      </Grid>
    </SmallCard>
  );
};
